import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updatingOrAddingServiceProblemCategory,
  updatingDueDatesByTicketType,
  updatingNoOfFollowOnTickets,
  updatingReorderPercentage,
  setManufacturerAndModelInList,
  readAsyncStorageValues,
} from "features/service/service.slice";
import { logout } from "features/auth/auth.slice";
import {
  Button,
  TextField,
  Box,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  useTheme,
  Stack,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Settings as SettingsIcon } from "@mui/icons-material";
import { Autocomplete, Checkbox } from "@mui/material";
import { toast } from "react-toastify";
import { deletingProblemCategory, uploadLicense, getLicenseInfo } from "features/service/service.slice";
import LicenseInfoCard from "component/LicenseInfoCard";

const ROW_LABEL_WIDTH_PX = 180;

const SettingRow = ({ label, children, sx }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${ROW_LABEL_WIDTH_PX}px 12px 1fr`,
        alignItems: "center",
        columnGap: 1,
        py: 0.25,
        ...sx,
      }}
    >
      <Typography
        variant="body2"
        noWrap
        sx={{
          textTransform: "capitalize",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2">:</Typography>
      <Box>{children}</Box>
    </Box>
  );
};

const Settings = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { appConstants, isLoading, license } = useSelector((state) => state.service);
  const { mode } = useSelector((state) => state.globalState);
  const licenseFileInputRef = React.useRef(null);
  const [selectedItem, setSelectedItem] = useState("");
  const [categoryName, setCategoryName] = useState({});
  const [problemCategories, setProblemCategories] = useState([]);
  const [dueDatesByTicketType, setDueDatesByTicketType] = useState([]);
  const [noOfFollowOnTickets, setNoOfFollowOnTickets] = useState([]);
  const [reorderPercentage, setReorderPercentage] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [open, setOpen] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showManufacturerModel, setShowManufacturerModel] = useState(() => {
    return JSON.parse(localStorage.getItem("showManufacturerModel")) ?? true;
  });

  useEffect(() => {
    if (appConstants?.serviceProblemCategory) {
      setProblemCategories(appConstants.serviceProblemCategory);
    }
    if (appConstants?.dueDatesByTicketType) {
      const dueDates = {};
      for (let item of appConstants?.dueDatesByTicketType) {
        Object.assign(dueDates, item);
      }
      const internalAuditCategory = appConstants?.allTypes?.SERVICE_CATEGORY?.INTERNAL_AUDIT;
      if (internalAuditCategory && dueDates[internalAuditCategory] === undefined) {
        dueDates[internalAuditCategory] = 7;
      }
      setDueDatesByTicketType(dueDates);
    }
    if (appConstants?.noOfFollowOnTickets) {
      setNoOfFollowOnTickets(appConstants.noOfFollowOnTickets);
    }
    if (appConstants?.reorderPercentage !== undefined && appConstants?.reorderPercentage !== null) {
      setReorderPercentage(appConstants?.reorderPercentage);
    }
  }, [appConstants]);


  const handleitemChange = (event, value) => {
    setSelectedItem(value);
    setCategoryName("");
  };

  const handleEditButtonClick = (index, category) => {
    setEditIndex(index);
    setCategoryName({ [category]: problemCategories[category] });
    setOpen(true);
  };

  const handleSaveDueDates = () => {
    const payloadlist = [];
    Object.keys(dueDatesByTicketType).forEach((element) => {
      payloadlist.push({ [element]: parseInt(dueDatesByTicketType[element]) });
    });
    const payload = {
      due_dates_by_ticket_type: payloadlist,
      configurable: { due_dates_by_ticket_type: { edit: true } },
    };

    dispatch(updatingDueDatesByTicketType(payload)).then((updateResponse) => {
      if (updateResponse.error) {
        toast.error("Failed to update due dates");
      } else {
        toast.success("Due dates updated successfully");
      }
    });
  };

  const handleSaveFollowOnTickets = () => {
    const payloadlist = [];
    for (const item of noOfFollowOnTickets) {
      const parsedCount = parseInt(item.count, 10);

      if (item.count === "" || item.count === null || item.count === undefined) {
        toast.warning("Follow-on ticket count cannot be empty");
        return;
      }

      if (isNaN(parsedCount) || parsedCount <= 0) {
        toast.warning("Follow-on ticket count must be greater than 0");
        return;
      }

      // ✅ CHANGED: Only push valid items
      payloadlist.push({ ...item, count: parsedCount });

    }
    const payload = {
      no_of_follow_on_tickets: payloadlist,
      configurable: { no_of_follow_on_tickets: { edit: true } },
    };

    dispatch(updatingNoOfFollowOnTickets(payload)).then((updateResponse) => {
      if (updateResponse.error) {
        toast.error("Failed to update follow-on tickets");
      } else {
        toast.success("Follow-on tickets updated successfully");
      }
    });
  };

  const handleSaveReorderPercentage = () => {
    const parsed = parseFloat(reorderPercentage);
    if (reorderPercentage === "" || reorderPercentage === null || reorderPercentage === undefined) {
      toast.warning("Reorder percentage cannot be empty");
      return;
    }
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast.warning("Reorder percentage must be between 0 and 100");
      return;
    }

    const payload = {
      reorder_percentage: parsed,
      configurable: { reorder_percentage: { edit: true } },
    };

    dispatch(updatingReorderPercentage(payload)).then((updateResponse) => {
      if (updateResponse.error) {
        toast.error("Failed to update reorder percentage");
      } else {
        dispatch(readAsyncStorageValues());
        toast.success("Reorder percentage updated successfully");
      }
    });
  };

  const handleAddOrEditConfirm = () => {
    if (Object.values(categoryName)[0].trim() !== "") {
      const isValidated = Object.keys(problemCategories).filter(
        (item) =>
          item.toLowerCase() !== Object.keys(categoryName)[0].toLowerCase() &&
          problemCategories[item].trim() === Object.values(categoryName)[0].trim()
      );
      if (!isValidated.length) {
        if (Object.keys(categoryName)[0] === "newKeyValue") updateCategories(categoryName, "add");
        else updateCategories(categoryName, "edit", editIndex);
      } else toast.warning("Problem category with same name already exists!");
    }
  };

  const handleEditCancel = () => {
    setEditIndex(null);
    setCategoryName({});
    setOpen(false);
  };

  const updateCategories = (updatedCategories, actionType, index = null) => {
    const payload = {
      configurable: { service_problem_category: appConstants.configurableServiceProblemCategory },
    };
    if (actionType === "add") payload.service_problem_category = updatedCategories;
    else if (actionType === "edit" && index !== null) payload.service_problem_category = updatedCategories;

    dispatch(updatingOrAddingServiceProblemCategory(payload)).then((updateResponse) => {
      if (updateResponse.error) {
        toast.error("Failed to update problem categories");
      } else {
        setCategoryName({});
        setEditIndex(null);
        setOpen(false);
        dispatch(readAsyncStorageValues()).then((res) => {
          if (res && res.payload && res.payload.status === 200 && res.payload.data?.resetPassword) {
            toast.warning(
              "You are logged out because you requested a reset password. Please set the new password from mobile app to continue.",
              { autoClose: 10000 }
            );
            dispatch(logout());
          }
        });
        toast.success("Problem categories updated successfully");
      }
    });
  };

  const handleFollowOnTicketChange = (index, key, value) => {
    const regex = /^[0-9\b]+$/;

    if (regex.test(value) || value === "") {
      const updatedFollowOnTickets = [...noOfFollowOnTickets];
      updatedFollowOnTickets[index] = { ...updatedFollowOnTickets[index], [key]: value };
      setNoOfFollowOnTickets(updatedFollowOnTickets);

      if (value === "0") {
        toast.warning("Follow-on ticket count must be greater than 0");
      }
    }
  };


  const handleDueDateChange = (key, value) => {
    const regex = /^[0-9\b]+$/;
    if (value === "" || regex.test(value)) {
      setDueDatesByTicketType({
        ...dueDatesByTicketType,
        [key]: value,
      });
    }
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDialog(true);
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  const handleConfirm = (category) => {
    onConfirmDelete(category);
    setShowDialog(false);
  };

  const onConfirmDelete = async (category) => {
    const payload = { service_problem_category: { [category]: problemCategories[category] } };
    dispatch(deletingProblemCategory(payload))
      .then((deleteRes) => {
        if (deleteRes && deleteRes.error) {
          return toast.error("Something went wrong!");
        }
        if (deleteRes && deleteRes.payload && deleteRes.payload.status === 200) {
          toast.success("Deleted successfully!", {
            autoClose: 1000,
          });
          dispatch(readAsyncStorageValues()).then((res) => {
            if (res && res.payload && res.payload.status === 200 && res.payload.data?.resetPassword) {
              toast.warning(
                "You are logged out because you requested a reset password. Please set the new password from mobile app to continue.",
                { autoClose: 10000 }
              );
              dispatch(logout());
            }
          });
        } else {
          return toast.error("Something went wrong!");
        }
      })
      .catch((error) => {
        toast.error("Something went wrong!");
      });
  };
  const handleCheckboxChange = async (event) => {
    const newValue = event.target.checked;
    setShowManufacturerModel(newValue);
    localStorage.setItem("showManufacturerModel", JSON.stringify(newValue));
    try {
      const updateResponse = dispatch(setManufacturerAndModelInList(newValue));
      const message = newValue
        ? "Manufacturer and Model will be shown in the Ticket List View"
        : "Manufacturer and Model will not be shown in the Ticket List View";
      updateResponse.error ? toast.error("Failed to update the setting. Please try again.") : toast.success(message);
    } catch (error) {
      toast.error("An error occurred while updating the setting.");
    }
  };

  const LICENSE_STATUS = appConstants?.allTypes?.LICENSE_STATUS || {};
  const isExpiredStatus =
    license?.licenseStatus === LICENSE_STATUS.EXPIRED || license?.licenseStatus === "Expired";
  const showExpiredView = Boolean(isExpiredStatus);

  const handleExpiredViewUploadClick = () => {
    if (licenseFileInputRef.current) {
      licenseFileInputRef.current.value = "";
      licenseFileInputRef.current.click();
    }
  };

  const handleExpiredViewLicenseFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const formData = new FormData();
    const licenseFile = new File([file], "license.dat", {
      type: file.type || "application/octet-stream",
    });
    formData.append("license", licenseFile);
    const resultAction = await dispatch(uploadLicense(formData));
    if (uploadLicense.fulfilled.match(resultAction)) {
      const payload = resultAction.payload || {};
      const success = payload.success === true;
      const message = payload.message || (success ? "License updated successfully" : "Something went wrong.");
      if (success) {
        await dispatch(getLicenseInfo({ force: true }));
        await dispatch(readAsyncStorageValues());
        toast.success(message);
      } else {
        toast.error(message);
      }
    } else {
      const errMsg = resultAction.payload || resultAction.error?.message || "Failed to upload license";
      toast.error(errMsg);
    }
  };

  return (
    <div>
      <Box sx={{ mx: 2, mt: 2 }}>
        {showExpiredView ? (
          <Paper sx={{ borderRadius: 3, mb: 2, p: 2.5, boxShadow: theme.shadows[2] }}>
            <Stack spacing={1.5}>
              <Typography variant="h5" fontWeight={700} color="error.main">
                License expired
              </Typography>
              <Typography variant="body1">
                Your license has expired and access to the application is temporarily disabled. Please upload a new license file to
                continue using the system.
              </Typography>
              <input
                type="file"
                accept=".dat"
                ref={licenseFileInputRef}
                style={{ display: "none" }}
                onChange={handleExpiredViewLicenseFileChange}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleExpiredViewUploadClick}
                disabled={license?.isUploading}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                Upload License
              </Button>
            </Stack>
          </Paper>
        ) : (
          <>
            <LicenseInfoCard />
            <Paper sx={{ borderRadius: 3, mb: 2, p: 2.5, boxShadow: theme.shadows[2] }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <SettingsIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  <Typography variant="h6" fontWeight={600}>
                    Settings
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction={"row"} mb={2.5}>
              <Autocomplete
                id="combo-box-demo"
                value={selectedItem}
                onChange={handleitemChange}
                options={[
                  "Problem category",
                  "Due Dates by Ticket Type",
                  "No of follow on tickets",
                  "Low Stock Reorder Percentage",
                  "Show Manufacturer Model In Ticket List",
                ].sort()}
                renderInput={(params) => <TextField {...params} label="Select an option" />}
                style={{ width: 350 }}
                size="small"
              />
              {selectedItem === "No of follow on tickets" && (
                <List dense sx={{ ml: 3, mt: -2 }}>
                  {noOfFollowOnTickets.map((followOnTicket, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 0.25 }}>
                      {Object.entries(followOnTicket).map(([key, value]) => (
                        <SettingRow
                          key={key}
                          label={key}
                          sx={{
                            minWidth: ROW_LABEL_WIDTH_PX + 12 + 220,
                          }}
                        >
                          <TextField
                            value={value}
                            onChange={(e) => handleFollowOnTicketChange(index, key, e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ width: 220 }}
                          />
                        </SettingRow>
                      ))}
                    </ListItem>
                  ))}
                </List>
              )}
              {selectedItem === "Low Stock Reorder Percentage" && (
                <Stack sx={{ ml: 3 }} direction="row" alignItems="center" columnGap={2}>
                  <TextField
                    label="Reorder Percentage"
                    type="number"
                    value={reorderPercentage}
                    onChange={(e) => setReorderPercentage(e.target.value)}
                    variant="outlined"
                    size="small"
                    disabled={!appConstants?.configurableReorderPercentage?.edit}
                    sx={{ width: 220 }}
                  />
                </Stack>
              )}
              {selectedItem === "Due Dates by Ticket Type" && (
                <Stack sx={{ ml: 3, mt: -2 }}>
                  <List dense>
                    {Object.values(appConstants?.allTypes?.SERVICE_CATEGORY).map((category, index) => (
                      <ListItem key={index} disableGutters sx={{ py: 0.25, display: "flex", alignItems: "center" }}>
                        {Object.keys(dueDatesByTicketType).includes(category) ? (
                          <SettingRow label={category} sx={{ width: "100%" }}>
                            <TextField
                              value={dueDatesByTicketType[category]}
                              onChange={(e) => handleDueDateChange(category, e.target.value)}
                              variant="outlined"
                              size="small"
                              sx={{ width: 220 }}
                            />
                          </SettingRow>
                        ) : null}
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              )}
              {selectedItem === "Problem category" && (
                <Box sx={{ ml: 3, display: "flex", alignItems: "flex-start" }}>
                  {appConstants?.configurableServiceProblemCategory?.add && (
                    <Stack direction={"row"} columnGap={2}>
                      <TextField
                        label="New Category"
                        value={categoryName?.newKeyValue || ""}
                        onChange={(e) => setCategoryName({ newKeyValue: e.target.value })}
                        variant="outlined"
                        size="small"
                      />
                      <Button onClick={() => handleAddOrEditConfirm()} variant="contained" disabled={isLoading}>
                        Add
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}
              <>
                {selectedItem === "Show Manufacturer Model In Ticket List" && (
                  <Box sx={{ ml: 3, display: "flex", alignItems: "flex-start" }}>
                    <Stack direction="row" alignItems="center">
                      <Checkbox
                        checked={showManufacturerModel}
                        onChange={handleCheckboxChange}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                      <Typography>Show Manufacturer Model In Ticket List</Typography>
                    </Stack>
                  </Box>
                )}
              </>
              <Stack sx={{ ml: 2 }}>
                {selectedItem === "Due Dates by Ticket Type" || selectedItem === "No of follow on tickets" || selectedItem === "Low Stock Reorder Percentage" ? (
                  <Button
                    onClick={() => {
                      if (selectedItem === "No of follow on tickets") return handleSaveFollowOnTickets();
                      if (selectedItem === "Due Dates by Ticket Type") return handleSaveDueDates();
                      if (selectedItem === "Low Stock Reorder Percentage") return handleSaveReorderPercentage();
                      return null;
                    }}
                    variant="contained"
                    color="primary"
                    disabled={selectedItem === "Low Stock Reorder Percentage" && !appConstants?.configurableReorderPercentage?.edit}
                    style={{ marginLeft: 10 }}
                  >
                    Save
                  </Button>
                ) : null}
              </Stack>
            </Stack>
              <Stack sx={{ ml: 1, mt: 1.5, flexWrap: "wrap" }}>
              {selectedItem === "Problem category" && (
                <Stack sx={{ flexDirection: "row", flexWrap: "wrap" }} columnGap={5} rowGap={1}>
                  {Object.keys(problemCategories)
                    .sort((a, b) => a.localeCompare(b))
                    .map((category, index) => (
                      <Stack direction={"row"} alignItems={"center"} minWidth={300}>
                        <Typography sx={{ flex: 1 }}>{problemCategories[category]}</Typography>
                        <Stack direction="row" columnGap={1}>
                          {appConstants?.configurableServiceProblemCategory?.edit && (
                            <IconButton onClick={() => handleEditButtonClick(index, category)} size="small">
                              <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
                            </IconButton>
                          )}
                          <IconButton onClick={() => handleDeleteClick(category)} s size="small">
                            <DeleteIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
                          </IconButton>
                          {/* <IconButton onClick={() => onConfirmDelete(category)} size="small">
                        <DeleteIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
                      </IconButton> */}
                        </Stack>
                      </Stack>
                    ))}
                </Stack>
              )}
            </Stack>
            <Dialog open={open} onClose={handleEditCancel}>
              <DialogTitle>Edit Category</DialogTitle>
              <DialogContent>
                <DialogContentText>Please confirm your edit for the category.</DialogContentText>
                <TextField
                  value={categoryName[Object.keys(categoryName)[0]]}
                  onChange={(e) => setCategoryName({ [Object.keys(categoryName)[0]]: e.target.value })}
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleEditCancel} color="primary">
                  Cancel
                </Button>
                <Button onClick={() => handleAddOrEditConfirm()} color="primary">
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
            <Dialog
              open={showDialog}
              onClose={handleCancel}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Are you sure you want to delete{" "}
                  <Typography component="span" color="text.primary">
                    {problemCategories[categoryToDelete]}
                  </Typography>
                  ?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={() => handleConfirm(categoryToDelete)} autoFocus>
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>
          </>
        )}
      </Box>
    </div>
  );
};

export default Settings;
